/*******************************************************************************
  TenantSiteStatus.js

  Author: Advait

 This component is responsible for managing status (enable/disable) of 
 sites or tenants.
*******************************************************************************/
import React, { useState, useEffect, useCallback } from "react";
import { showNotification } from "./helper/notification";
import { API, graphqlOperation } from "aws-amplify";
import { fetchAllPages } from "../apihelpers/graphqlhelpers";

const QUERIES = {
  site: {
    list: /* GraphQL */ `
      query ListSites($filter: ModelSiteFilterInput, $limit: Int, $nextToken: String) {
        listSites(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items {
            id
            tid
            siteName
            disabled
          }
          nextToken
        }
      }
    `,
    listName: "listSites",
    update: /* GraphQL */ `
      mutation UpdateSite($input: UpdateSiteInput!) {
        updateSite(input: $input) {
          id
          tid
          siteName
          disabled
          updatedAt
        }
      }
    `,
    nameField: "siteName",
    title: "Site Status",
  },
  tenant: {
    list: /* GraphQL */ `
      query ListTenants($filter: ModelTenantFilterInput, $limit: Int, $nextToken: String) {
        listTenants(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items {
            id
            company
            disabled
            sites {
              items {
                id
                siteName
                disabled
              }
            }
          }
          nextToken
        }
      }
    `,
    listName: "listTenants",
    update: /* GraphQL */ `
      mutation UpdateTenant($input: UpdateTenantInput!) {
        updateTenant(input: $input) {
          id
          company
          disabled
          updatedAt
        }
      }
    `,
    nameField: "company",
    title: "Edit Tenant",
  },
};

function TenantSiteStatus({ entityType, tenantId }) {
  const [entityList, setEntityList] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [entityDisabled, setEntityDisabled] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);

  const fetchEntities = useCallback(async () => {
    const config = QUERIES[entityType];
    if (!config) {
      console.error(`Invalid entityType: ${entityType}`);
      return;
    }

    if (entityType === "site" && !tenantId) {
      setEntityList([]);
      setSelectedEntityId("");
      return;
    }

    try {
      let args = { limit: 100 };

      // Add tenant filter for Manage Sites only
      if (entityType === "site" && tenantId) {
        args.filter = { tid: { eq: tenantId } };
      }

      let entities = await fetchAllPages(config.list, config.listName, args);

      // Save current selection and update disabled status to
      // prevent losing it after refresh
      const updatedEntity = selectedEntityId
        ? entities.find((e) => e.id === selectedEntityId)
        : null;

      setEntityList(entities);
      setEntityDisabled(updatedEntity ? updatedEntity.disabled : false);
    } catch (e) {
      console.log(`Error fetching ${entityType}s:`, e);
      setEntityList([]);
    }
  }, [entityType, tenantId, selectedEntityId]);

  useEffect(() => {
    if (entityType === "tenant" || tenantId) {
      fetchEntities();
    }
  }, [fetchEntities, tenantId, entityType]);

  const handleSelectEntity = (event) => {
    const entityId = event.target.value;
    if (entityId) {
      const selectedEntity = entityList.find((entity) => entity.id === entityId);
      setSelectedEntityId(entityId);
      setEntityDisabled(selectedEntity ? selectedEntity.disabled : false);
      if (entityType === "tenant" && selectedEntity) {
        setCompanyName(selectedEntity.company || "");
        setSaveButtonDisabled(true);
      }
    } else {
      setSelectedEntityId("");
      setCompanyName("");
      setSaveButtonDisabled(true);
    }
  };

  const handleToggleChange = async () => {
    if (!selectedEntityId) {
      return;
    }

    // For tenant, just update state
    if (entityType === "tenant") {
      setEntityDisabled(!entityDisabled);
      setSaveButtonDisabled(false);
      return;
    }

    // For site, save immediately
    const config = QUERIES[entityType];
    const selectedEntity = entityList.find((entity) => entity.id === selectedEntityId);
    const newStatus = !entityDisabled;
    const action = newStatus ? "disable" : "enable";
    const entityName = selectedEntity
      ? selectedEntity[config.nameField]
      : `this ${entityType}`;

    try {
      // Update the tenant/site
      await API.graphql(
        graphqlOperation(config.update, {
          input: {
            id: selectedEntityId,
            disabled: newStatus,
          },
        })
      );

      showNotification(true, `"${entityName}" ${action}d successfully`);
      // Refresh the entities list
      await fetchEntities();
    } catch (e) {
      console.log(`Error updating ${entityType}:`, e);
      showNotification(false, `Failed to ${action} "${entityName}". Please try again.`);
    }
  };

  const handleChangeCompany = (event) => {
    setCompanyName(event.target.value);
    setSaveButtonDisabled(event.target.value.trim() === "");
  };

  const handleSaveTenant = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    setSaveButtonDisabled(true);

    const config = QUERIES[entityType];
    const selectedEntity = entityList.find((entity) => entity.id === selectedEntityId);
    const trimmedCompanyName = companyName.trim();

    try {
      // Update the tenant
      const response = await API.graphql(
        graphqlOperation(config.update, {
          input: {
            id: selectedEntityId,
            company: trimmedCompanyName,
            disabled: entityDisabled,
          },
        })
      );

      console.log(`${entityType} updated:`, response);

      // If disabling a tenant, also disable all its sites
      if (entityDisabled === true && selectedEntity.sites?.items) {
        const siteUpdateMutation = /* GraphQL */ `
          mutation UpdateSite($input: UpdateSiteInput!) {
            updateSite(input: $input) {
              id
              disabled
            }
          }
        `;

        // Update all sites to disabled
        const siteUpdatePromises = selectedEntity.sites.items.map((site) =>
          API.graphql(
            graphqlOperation(siteUpdateMutation, {
              input: {
                id: site.id,
                disabled: true,
              },
            })
          )
        );

        await Promise.all(siteUpdatePromises);
        showNotification(true, `"${trimmedCompanyName}" and its sites disabled successfully`);
      } else {
        showNotification(true, `"${trimmedCompanyName}" updated successfully`);
      }

      // Refresh the entities list
      await fetchEntities();
    } catch (e) {
      console.log(`Error updating ${entityType}:`, e);
      showNotification(false, `Failed to update "${trimmedCompanyName}". Please try again.`);
      setSaveButtonDisabled(companyName.trim() === "");
    }
  };

  const config = QUERIES[entityType];
  if (!config) {
    return null;
  }

  return (
    <div className="individualContainer">
      <table className="individualContainerTable">
        <tbody>
          <tr>
            <td className="individualContainerTitleColumn" valign="top">
              <h4 className="individualContainerTitleHeading">{config.title}</h4>
            </td>
            <td>
              <table>
                <tbody>
                  <tr>
                    <td
                      className="individualContainerSubTitleColumn"
                      style={{ verticalAlign: "middle" }}
                    >
                      <h6>
                        Select {entityType[0].toUpperCase() + entityType.slice(1)} to Manage
                      </h6>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <select
                        className="inputFieldSelect"
                        value={selectedEntityId}
                        onChange={handleSelectEntity}
                      >
                        <option value="">
                          -- Please Choose a{" "}
                          {entityType[0].toUpperCase() + entityType.slice(1)} --
                        </option>
                        {entityList
                          .filter((entity) => !entity.disabled)
                          .sort((a, b) =>
                            a[config.nameField].localeCompare(b[config.nameField])
                          )
                          .map((entity) => (
                            <option key={entity.id} value={entity.id}>
                              {entity[config.nameField]}
                            </option>
                          ))}
                        {entityList.filter((entity) => entity.disabled).length > 0 && (
                          <option disabled>────────────────</option>
                        )}
                        {entityList
                          .filter((entity) => entity.disabled)
                          .sort((a, b) =>
                            a[config.nameField].localeCompare(b[config.nameField])
                          )
                          .map((entity) => (
                            <option key={entity.id} value={entity.id}>
                              {entity[config.nameField]} (disabled)
                            </option>
                          ))}
                      </select>
                    </td>
                  </tr>
                  {selectedEntityId ? (
                    <>
                      {entityType === "tenant" && (
                        <tr>
                          <td
                            className="individualContainerSubTitleColumn"
                            style={{ verticalAlign: "middle" }}
                          >
                            <h6>Company Name</h6>
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <input
                              className="inputField"
                              style={{ paddingLeft: "5px" }}
                              type="text"
                              placeholder="Company name"
                              value={companyName}
                              onChange={handleChangeCompany}
                            />
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td
                          className="individualContainerSubTitleColumn"
                          style={{ verticalAlign: "middle" }}
                        >
                          <h6>Status</h6>
                        </td>
                        <td className="toggleSwitchContainer">
                          <label className="toggleSwitchLabel">
                            <input
                              type="checkbox"
                              checked={!entityDisabled}
                              onChange={handleToggleChange}
                              className="toggleSwitchCheckbox"
                            />
                            <span
                              className={`toggleSwitchSlider ${
                                entityDisabled ? "inactive" : "active"
                              }`}
                            >
                              <span
                                className={`toggleSwitchKnob ${
                                  entityDisabled ? "inactive" : "active"
                                }`}
                              />
                            </span>
                          </label>
                          <span className="toggleSwitchStatusLabel">
                            {entityDisabled ? "Disabled" : "Active"}
                          </span>
                        </td>
                      </tr>
                    </>
                  ) : null}
                </tbody>
              </table>
            </td>
          </tr>
          {/* {Save button for tenant} */}
          {entityType === "tenant" && selectedEntityId && (
            <tr>
              <td className="individualContainerTitleColumn" valign="top"></td>
              <td style={{ padding: "0 0 15px 0" }}>
                <button
                  className="saveButton"
                  type="submit"
                  onClick={handleSaveTenant}
                  disabled={saveButtonDisabled}
                >
                  Save
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default TenantSiteStatus;
